const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-color-muted: var(--ctl-color-muted);
    --ctl-color-brand: var(--ctl-color-brand);
    --ctl-focus-ring: var(--ctl-focus-ring);
    --ctl-radius-sm: var(--ctl-radius-sm);

    display: inline;
    font-family: var(--ctl-font);
  }

  a{
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xxs);
    color: var(--ctl-color-brand);
    text-decoration: none;
    font-weight: var(--ctl-font-weight-bold);
    border-radius: var(--ctl-radius-sm);
    outline: none;
  }

  a:hover{
    color: color-mix(in srgb, var(--ctl-color-brand) 78%, var(--ctl-color-text));
  }

  a:focus-visible{
    box-shadow: var(--ctl-focus-ring);
  }

  /* underline modes */
  :host([underline="always"]) a{ text-decoration: underline; text-underline-offset: calc(var(--spacing-xxs) / 2); }
  :host([underline="never"])  a{ text-decoration: none; }
  :host([underline="auto"])   a:hover{ text-decoration: underline; text-underline-offset: calc(var(--spacing-xxs) / 2); }

  /* disabled */
  :host([disabled]) a{
    color: var(--ctl-color-muted);
    cursor: not-allowed;
    text-decoration: none;
    pointer-events: none;
  }

  .icon{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: currentColor;
  }
</style>

<a id="a">
  <span class="icon"><slot name="icon"></slot></span>
  <span class="label"><slot></slot></span>
</a>
`;

function normalizeRelForBlankTarget(rel) {
  const tokens = new Set(String(rel || "").split(/\s+/).filter(Boolean));
  tokens.add("noopener");
  tokens.add("noreferrer");
  return Array.from(tokens).join(" ");
}

export class CtlLink extends HTMLElement {
  static get observedAttributes() {
    return ["href", "target", "rel", "disabled", "underline"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._a = this.shadowRoot.getElementById("a");
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("underline")) this.setAttribute("underline", "auto");
    this._a.addEventListener("click", this._onClick);
    this._a.addEventListener("keydown", this._onKeyDown);
    this._sync();
  }

  disconnectedCallback() {
    this._a.removeEventListener("click", this._onClick);
    this._a.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get href() { return this.getAttribute("href") || ""; }
  set href(v) { this.setAttribute("href", String(v ?? "")); }

  get target() { return this.getAttribute("target") || ""; }
  set target(v) { v == null || v === "" ? this.removeAttribute("target") : this.setAttribute("target", String(v)); }

  get rel() { return this.getAttribute("rel") || ""; }
  set rel(v) { v == null || v === "" ? this.removeAttribute("rel") : this.setAttribute("rel", String(v)); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get underline() { return this.getAttribute("underline") || "auto"; }
  set underline(v) { this.setAttribute("underline", String(v || "auto")); }

  focus() {
    // only focusable when enabled
    if (!this.disabled) this._a.focus();
  }

  _sync() {
    const href = this.href;
    const target = this.target;
    const rel = this.rel;
    const disabled = this.disabled;

    // required-ish behavior: if no href, treat as disabled to avoid broken nav
    const effectiveDisabled = disabled || !href;

    if (effectiveDisabled) {
      this._a.removeAttribute("href");
      this._a.removeAttribute("target");
      this._a.removeAttribute("rel");
      this._a.setAttribute("aria-disabled", "true");
      this._a.tabIndex = -1;
      // Only set disabled attribute if not already present (avoid infinite recursion)
      if (!this.hasAttribute("disabled")) {
        this.setAttribute("disabled", "");
      }
      return;
    }

    // enabled
    this._a.setAttribute("href", href);
    this._a.removeAttribute("aria-disabled");
    this._a.tabIndex = 0;

    if (target) this._a.setAttribute("target", target);
    else this._a.removeAttribute("target");

    // security: auto rel for target=_blank unless user provided rel
    if (target === "_blank") {
      const safeRel = rel ? rel : "noopener noreferrer";
      this._a.setAttribute("rel", normalizeRelForBlankTarget(safeRel));
    } else {
      if (rel) this._a.setAttribute("rel", rel);
      else this._a.removeAttribute("rel");
    }

    // underline normalization - only normalize invalid values to prevent recursion
    const u = this.underline;
    if (u !== "auto" && u !== "always" && u !== "never") {
      this.setAttribute("underline", "auto");
    }
  }

  _emitClickEvent() {
    const href = this.href;
    this.dispatchEvent(new CustomEvent("ctl-click", {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { href }
    }));
  }

  _onClick(e) {
    // disabled or missing href -> no nav
    if (this.disabled || !this.href) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // emit; allow consumer to preventDefault if they want to intercept routing
    const ev = new CustomEvent("ctl-click", {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { href: this.href }
    });
    this.dispatchEvent(ev);
    if (ev.defaultPrevented) e.preventDefault();
  }

  _onKeyDown(e) {
    // For completeness: if disabled, prevent enter activation
    if (this.disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
}

customElements.define("ctl-link", CtlLink);
